// Import required libraies
import React, { useState, useRef, useEffect } from "react";
import { v4 } from "uuid";

//import library components
import { makeStyles } from "@material-ui/core";

// Import Custom Component
import AppCard from "../AppCard";
import SigviewButton from "../Common/SigviewButton";
import SigviewTypography from "../Common/SigviewTypography";

// Import utils & data
import { getElementContentWidth } from "../../utils/utils";
import SigviewDataSort from "../Common/SigviewDataSort";
import { masterTrackGaEvent } from "../../services/ga";

// Defining styles
const useSigviewStyles = makeStyles((style) => {
  const styles = {
    mainContainer: {
      padding: (style) => style.padding || "25px",
      "&.title-row-less": { paddingTop: "5px" },
    },
    titleContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "10px",
    },
    itemsContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
      justifyItems: "start",
      gridGap: (style) => style.gridGap || "18px",
    },
    renderPropContainer: {
      display: "flex",
      width: "100%",
      height: "125px",
      justifyContent: "center",
      alignItems: "center",
    },
  };
  return styles;
});

function AppRow(props) {
  // Destructing props
  const {
    title,
    onButtonClick,
    titleRowFlag,
    buttonRowFlag,
    sortButtonProps,
    singleRowFlag,
    data,
    buttonTitle = "Show all",
    renderProps = { flag: false, component: () => {} },
    style={},
  } = props;

  // Defining required states
  const containerId = useRef(`appRow_${v4()}`);
  const [sliceCount, setSliceCount] = useState(data.length);
  const [sortedData, setSortedData] = useState(data);

  // Defining required side effects
  useEffect(() => {
    // Defining required function
    const handleWindowSize = () => {
      // Should come from config
      const appCardWidth = 180;
      const appRowGridGap = 12;

      // Getting slice count based on width
      const appRowEl = document.getElementById(containerId.current);
      const appRowContentWidth = getElementContentWidth(appRowEl);
      const newSliceCount = Math.floor(
        appRowContentWidth / (appCardWidth + appRowGridGap)
      );

      // Setting slice count
      setSliceCount(newSliceCount);
    };

    // Do this only when singleRowFlag is true
    if (singleRowFlag) {
      // Call the function when component mounts
      handleWindowSize();

      // Add event listener to window resize
      window.addEventListener("resize", handleWindowSize);
    }

    return () => {
      // Remove event listener to window resize
      window.removeEventListener("resize", handleWindowSize);
    };
  }, []);
  // Update slice count everytime data changes
  useEffect(() => {
    if (!singleRowFlag) setSliceCount(data.length);
    setSortedData(data);
    //sort based on most recent update
    handleSaveDataSort({sortType: { id: "_updatedOn"}, sortTypeValue: "asc",})
  }, [data]);

  var sortTypeData =  [
    {id: "_updatedOn", name: "Last Update", disabled: false},
    {id: "_title", name: "Pivot Title", disabled: false}
  ];
  const dataForDataSort = {
    sortType: sortTypeData,
    // sortTypeId: [{ id: "id_only", name: "Value", disabled: false }],
  };
  const initialSelectionsDataSort = {
    sortType: { id: "_updatedOn"},
    sortTypeId: { id: "id_only" },
    sortTypeValue: "asc",
  };

  const sortByTitle = (order) => {
    if (data.length <= 1) return;

    // Keep the first item untouched and sort the rest
    const [firstItem, ...rest] = data;
    const sortedRest = rest.sort((a, b) => {
      const titleA = a.payload?.title?.toLowerCase() || '';
      const titleB = b.payload?.title?.toLowerCase() || '';
      return order === 'asc' ? 
      titleA.localeCompare(titleB, undefined, { numeric: true }) : 
      titleB.localeCompare(titleA, undefined, { numeric: true });
    });

    setSortedData([firstItem, ...sortedRest]);
  }

  const sortByLastUpdate = (order) => {
    if (data.length <= 1) return;
    const [firstItem, ...rest] = data;
    const sortedRest = rest.sort((a, b) => {
      const timeA = a.payload?.updatedOn;
      const timeB = b.payload?.updatedOn;
      return order === 'asc' ? timeB - timeA : timeA - timeB;
    });

    setSortedData([firstItem, ...sortedRest]);
  }

  const handleSaveDataSort = (value) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "Pivot",
      action: "AppCardSort",
      label: value.sortTypeValue,
    });

    if(value.sortType.id === "_title") sortByTitle(value.sortTypeValue);
    if(value.sortType.id === "_updatedOn") sortByLastUpdate(value.sortTypeValue);
  };

  // Defining required static variables
  let {
    mainContainer,
    titleContainer,
    itemsContainer: itemsContainerClassName,
    renderPropContainer,
  } = useSigviewStyles(style);
  if (!titleRowFlag) mainContainer += " title-row-less";
  if (renderProps.flag) itemsContainerClassName = renderPropContainer;

  var datastoryStateTitleClass = title.replace(/ +/g, "");

  return (
    <div className={mainContainer} id={containerId.current}>
      {titleRowFlag && (
        <section className={titleContainer}>
          <SigviewTypography variant="heading" style={{fontSize: "16px", height: "max-content"}}>{title}</SigviewTypography>
          <div
            style={{display: "flex", gap: "10px", alignItems: "center"}}
          >
            {sortButtonProps.sortButtonFlag && (
                <SigviewDataSort
                  data={dataForDataSort}
                  initialSelections={initialSelectionsDataSort}
                  handleSave={handleSaveDataSort}
                  sortTitle="Sort On:"
                  sortByTitle="Sort Order:"
                  iconBoxFlag={true}
                  tooltipTitle={sortButtonProps.sortButtonTitle}
                  sortTypeIdFlag={false}
                />
            )}
            {buttonRowFlag && (
                <SigviewButton
                variant="containedLighter"
                onClick={onButtonClick}
                title={buttonTitle}
                customClassName={`${datastoryStateTitleClass}SeeAll-GA`}
                style={{
                  buttonHeight: "32px",
                  padding: "10px 12px",
                  borderRadius: "6px",
                }}
              />
            )}
          </div>
        </section>
      )}
      <section className={itemsContainerClassName}>
        {renderProps.flag && renderProps.component()}
        {!renderProps.flag &&
          sortedData
            .slice(0, sliceCount)
            .map((row) => (
              <AppCard
                datastoryStateTitleClass={datastoryStateTitleClass}
                key={row.id}
                {...row}
              />
            ))}
      </section>
    </div>
  );
}

export default AppRow;
